import { DEFAULT_PER_PAGE_OPTIONS } from '../../components/paginationItems';
import {
  MLD_DEFAULT_PER_PAGE,
  MLD_FAMILY_FILTERS,
  MLD_STATUS_FILTERS,
  type MldFamilyFilter,
  type MldSortBy,
  type MldSortDir,
  type MldStatusFilter,
} from '../../api/mld/types';

/**
 * State of the `/mld` list (tab, chips, search, page, page size and sort),
 * kept in the URL query so a reload, a shared link, the browser's back and
 * forward buttons and the deal's Back link all return to the same list
 * (ADR-004).
 *
 * Pure on purpose: no React and no router import, so every rule is
 * unit-tested; the page only wires these helpers to `useSearchParams`.
 * Modelled on Customer Information's `customerListQuery.ts`.
 */

/** The list tab: a DS status filter, or `all` (no status filter). */
export type MldTab = MldStatusFilter | 'all';

/** The sortable date columns; `activity` is the DS default, never a header. */
export type MldSortColumn = Exclude<MldSortBy, 'activity'>;

/**
 * The direction a column starts in on its first click (ADR-001). ABO's own
 * copy of the DS `MLD_SORT_FIRST_DIR`, since ABO cannot import DS code.
 */
export const MLD_SORT_FIRST_DIR: Readonly<Record<MldSortColumn, MldSortDir>> =
  Object.freeze({
    lastPayment: 'desc',
    upcoming: 'asc',
    since: 'asc',
  });

/** Page sizes the list offers: those of the pagination selector (10, 15, 25, 50). */
export const MLD_LIST_PER_PAGE_OPTIONS: readonly number[] = Object.freeze([
  ...DEFAULT_PER_PAGE_OPTIONS,
]);

export interface MldListUrlState {
  tab: MldTab;
  family: MldFamilyFilter | '';
  cancelsSoon: boolean;
  trialSale: boolean;
  /** Free-text search, as typed. */
  q: string;
  /** 1-based. */
  page: number;
  perPage: number;
  /** `''` means no header was clicked. */
  sortBy: MldSortColumn | '';
  /** `''` means the column's first direction. */
  sortDir: MldSortDir | '';
}

/** Where the deal detail's Back link leads (router state `mldBack`). */
export interface MldBack {
  kind: 'list' | 'member';
  to: string;
}

export const MLD_LIST_PATH = '/mld';

export const DEFAULT_MLD_LIST_URL_STATE: Readonly<MldListUrlState> =
  Object.freeze({
    tab: 'all',
    family: '',
    cancelsSoon: false,
    trialSale: false,
    q: '',
    page: 1,
    perPage: MLD_DEFAULT_PER_PAGE,
    sortBy: '',
    sortDir: '',
  });

/** URL keys, one per `MldListUrlState` field. */
export const MLD_LIST_URL_KEYS = {
  tab: 'tab',
  family: 'family',
  cancelsSoon: 'cancelsSoon',
  trialSale: 'trialSale',
  q: 'q',
  page: 'page',
  perPage: 'perPage',
  sortBy: 'sortBy',
  sortDir: 'sortDir',
} as const satisfies Record<keyof MldListUrlState, string>;

const MLD_TABS: readonly MldTab[] = ['all', ...MLD_STATUS_FILTERS];

const MLD_SORT_COLUMNS = Object.keys(MLD_SORT_FIRST_DIR) as MldSortColumn[];

const SORT_DIRECTIONS: readonly MldSortDir[] = ['asc', 'desc'];

/** Case-insensitive match for lower-case vocabularies (`tab`, `sortDir`). */
const oneOf = <T extends string>(
  allowed: readonly T[],
  raw: string | null,
  fallback: T | '',
): T | '' => {
  const value = (raw ?? '').trim().toLowerCase();
  return (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
};

/** Exact match for upper-case and camelCase vocabularies (`family`, `sortBy`). */
const exactOneOf = <T extends string>(
  allowed: readonly T[],
  raw: string | null,
): T | '' =>
  raw !== null && (allowed as readonly string[]).includes(raw)
    ? (raw as T)
    : '';

const positiveInt = (raw: string | null, fallback: number): number => {
  if (raw === null || !/^\d+$/.test(raw.trim())) return fallback;
  const value = Number(raw.trim());
  return Number.isSafeInteger(value) && value >= 1 ? value : fallback;
};

const flag = (raw: string | null): boolean => {
  const value = (raw ?? '').trim().toLowerCase();
  return value === '1' || value === 'true';
};

/**
 * Reads the list state from the URL. Every unknown or invalid value falls
 * back silently to its default, so an old bookmark never breaks the list.
 */
export function readMldListUrlState(params: URLSearchParams): MldListUrlState {
  const K = MLD_LIST_URL_KEYS;
  const D = DEFAULT_MLD_LIST_URL_STATE;
  const perPage = positiveInt(params.get(K.perPage), D.perPage);
  return {
    tab: (oneOf(MLD_TABS, params.get(K.tab), D.tab) || D.tab) as MldTab,
    family: exactOneOf(MLD_FAMILY_FILTERS, params.get(K.family)),
    cancelsSoon: flag(params.get(K.cancelsSoon)),
    trialSale: flag(params.get(K.trialSale)),
    q: params.get(K.q) ?? D.q,
    page: positiveInt(params.get(K.page), D.page),
    perPage: MLD_LIST_PER_PAGE_OPTIONS.includes(perPage) ? perPage : D.perPage,
    sortBy: exactOneOf(MLD_SORT_COLUMNS, params.get(K.sortBy)),
    sortDir: oneOf(SORT_DIRECTIONS, params.get(K.sortDir), D.sortDir),
  };
}

/**
 * Writes the list state to URL params, leaving out every value equal to its
 * default; the booleans are written as `1`.
 */
export function writeMldListUrlState(state: MldListUrlState): URLSearchParams {
  const params = new URLSearchParams();
  const K = MLD_LIST_URL_KEYS;
  const D = DEFAULT_MLD_LIST_URL_STATE;
  (Object.keys(K) as (keyof MldListUrlState)[]).forEach((field) => {
    const value = state[field];
    if (value === D[field] || value === '' || value === false) return;
    params.set(K[field], value === true ? '1' : String(value));
  });
  return params;
}

/**
 * Applies a change to the list state. Any change of a field other than
 * `page` sends the list back to page 1 (a filtered or re-sorted list starts
 * at the top); a patch that only moves the page keeps everything else.
 */
export function withChange(
  state: MldListUrlState,
  patch: Partial<MldListUrlState>,
): MldListUrlState {
  const next = { ...state, ...patch };
  const changesMoreThanPage = (
    Object.keys(patch) as (keyof MldListUrlState)[]
  ).some((field) => field !== 'page' && patch[field] !== state[field]);
  if (changesMoreThanPage) return { ...next, page: 1 };
  return { ...next, page: positiveInt(String(next.page), 1) };
}

/**
 * The sort the list shows and requests: the clicked header if any; else,
 * with "Cancels soon" on, Cancels on soonest first; else `null`, which means
 * the DS default (newest activity first) and no active header.
 */
export function effectiveMldSort(
  state: MldListUrlState,
): { by: MldSortColumn; dir: MldSortDir } | null {
  if (state.sortBy !== '') {
    return {
      by: state.sortBy,
      dir: state.sortDir || MLD_SORT_FIRST_DIR[state.sortBy],
    };
  }
  if (state.cancelsSoon) return { by: 'upcoming', dir: 'asc' };
  return null;
}

/**
 * Header click on a sortable column: the column the list is sorted by
 * (including `upcoming` under the "Cancels soon" default) flips its
 * direction; any other column starts at its first direction. Both go back
 * to page 1.
 */
export function toggleSort(
  state: MldListUrlState,
  column: MldSortColumn,
): MldListUrlState {
  const effective = effectiveMldSort(state);
  const sortDir: MldSortDir =
    effective?.by === column
      ? effective.dir === 'asc'
        ? 'desc'
        : 'asc'
      : MLD_SORT_FIRST_DIR[column];
  return { ...state, sortBy: column, sortDir, page: 1 };
}

const LIST_TARGET = /^\/mld(\?.*)?$/;
const MEMBER_TARGET = /^\/customer-information\/[0-9a-fA-F]{24}$/;

const FALLBACK_BACK: Readonly<MldBack> = Object.freeze({
  kind: 'list',
  to: MLD_LIST_PATH,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Reads the Back target from the router state (`state.mldBack`). Only the
 * list (`/mld` with an optional query) and a member page
 * (`/customer-information/<24-hex id>`) are accepted, each with its own
 * kind; anything else, or no state at all (a new tab, a pasted URL), falls
 * back to the list, so the link can never lead off ABO or up the path.
 */
export function readMldBack(state: unknown): MldBack {
  const back = isRecord(state) ? state.mldBack : undefined;
  if (!isRecord(back) || typeof back.to !== 'string') {
    return { ...FALLBACK_BACK };
  }
  const { kind, to } = back;
  if (kind === 'list' && LIST_TARGET.test(to)) return { kind, to };
  if (kind === 'member' && MEMBER_TARGET.test(to)) return { kind, to };
  return { ...FALLBACK_BACK };
}
